import React, { useCallback, useEffect, useState } from 'react';
import CompanyPickerDialog from './CompanyPickerDialog';
import NexumiLogo from '../common/NexumiLogo';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Box, Typography,
  InputAdornment, Stack, IconButton, CircularProgress, Slide, Divider,
} from '@mui/material';
import MailOutlineIcon        from '@mui/icons-material/MailOutline';
import LockOutlinedIcon       from '@mui/icons-material/LockOutlined';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import VisibilityOffIcon      from '@mui/icons-material/VisibilityOff';
import DomainAddIcon          from '@mui/icons-material/DomainAdd';
import GoogleIcon             from '@mui/icons-material/Google';
import GetAppIcon             from '@mui/icons-material/GetApp';
import ArrowBackIcon          from '@mui/icons-material/ArrowBack';
import CloseIcon              from '@mui/icons-material/Close';
import IosShareIcon           from '@mui/icons-material/IosShare';

import { usePWAInstall }      from '../../hooks/usePWAInstall';
import Toast                  from '../actions/Toasts';
import { persistLogedInUser, isElectron } from '../../services/sessionPersistence';
import apiService             from '../../services/apiServices';
import { useForm }            from 'react-hook-form';
import * as yup               from 'yup';
import { yupResolver }        from '@hookform/resolvers/yup';
import ForgotPassword         from './ForgotPassword';
import { useTranslation }     from 'react-i18next';

// Microsoft 4-square brand mark (no @mui/icons-material equivalent).
const MicrosoftMark = () => (
  <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
    <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
    <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const schema = yup.object().shape({
  // Pranon email OSE ID/kod punëtori. Nëse përmban '@', duhet të jetë email i vlefshëm.
  Email:    yup.string().required('Email ose ID e punëtorit është i detyrueshëm')
    .test('email-or-id', 'Email i pavlefshëm', (v) => (v && v.includes('@')) ? yup.string().email().isValidSync(v) : true),
  Password: yup.string().required('Fjalëkalimi është i detyrueshëm'),
});

// Të gjitha fushat janë të detyrueshme me qëllim: ky formular hap një DATABAZË
// të vërtetë me demo 5-ditore, dhe një kompani pa qytet, telefon e person
// kontaktues është një klient që nuk kontaktohet dot kur demo-ja mbaron.
const tenantSchema = yup.object().shape({
  TenantName:     yup.string().required('Emri i kompanisë është i detyrueshëm'),
  TenantEmail:    yup.string().email('Email i pavlefshëm').required('Email i detyrueshëm'),
  TenantSlug:     yup.string().nullable().optional(),
  TenantPassword: yup.string().min(8, 'Të paktën 8 karaktere').required('Fjalëkalimi është i detyrueshëm'),
  TenantPhone:    yup.string().required('Numri i telefonit është i detyrueshëm'),
  TenantManager:  yup.string().required('Personi kontaktues është i detyrueshëm'),
  // Qyteti dhe adresa nuk kërkohen më te hapja: plotësohen brenda te Cilësimet.
  TenantCity:     yup.string().nullable().optional(),
  TenantAddress:  yup.string().nullable().optional(),
});

const checkSlugAvailability = async (slug, onStatus, onMessage) => {
  if (!slug) { onStatus(null); onMessage(''); return { available: false }; }
  if (!/^[a-z]/.test(slug)) { onStatus('used'); onMessage('Slug duhet të fillojë me shkronjë.'); return { available: false }; }
  onStatus('checking'); onMessage('Duke kontrolluar...');
  try {
    // `/api/query/checkTenantSlug` rri pas `requireAuth`, kurse ky formular
    // shfaqet te ekrani i kyçjes — pra përgjigjja ishte gjithmonë 401 dhe çdo
    // slug dukej i zënë. Rruga publike `slug-available` e kthen përgjigjen e
    // vërtetë, dhe kontrollon TË DY regjistrat.
    const backendUrl = process.env.REACT_APP_API_URL || '';
    const resp = await fetch(
      `${backendUrl}/api/tenants/slug-available?slug=${encodeURIComponent(slug)}`,
    ).then((r) => r.json());
    const available = resp?.success === true && resp?.available === true;
    if (available) { onStatus('free'); onMessage('Slug është i lirë ✓'); }
    else { onStatus('used'); onMessage('Ky slug është i zënë.'); }
    return { available };
  } catch {
    onStatus(null); onMessage('Gabim në kontrollim.');
    return { available: false };
  }
};

export default function SignIn({ onBack, initialEmail }) {
  const [toast,              setToast]              = useState({ open: false, message: '', variant: '' });
  const { t }                                       = useTranslation();
  const [loading,            setLoading]            = useState(false);
  const [showPass,           setShowPass]           = useState(false);
  const [tenantModal,        setTenantModal]        = useState(false);
  const [forgotModal,        setForgotModal]        = useState(false);
  const [slugStatus,         setSlugStatus]         = useState(null);
  const [slugMessage,        setSlugMessage]        = useState('');
  const [tenantSlugManual,   setTenantSlugManual]   = useState(false);
  const [tenantLoading,      setTenantLoading]      = useState(false);
  const [pwaVisible,         setPwaVisible]         = useState(true);
  // Show each provider's button only when the backend reports it configured
  // (enabled + credentials, per the company's Settings) — never on a build flag
  // alone. Avoids sending users to a raw "not configured" JSON error.
  const [googleSsoReady,     setGoogleSsoReady]     = useState(false);
  const [microsoftSsoReady,  setMicrosoftSsoReady]  = useState(false);

  const { isInstallable, isIOS, needsManual, isInStandaloneMode, install } = usePWAInstall();

  useEffect(() => {
    let alive = true;
    const backendUrl = process.env.REACT_APP_API_URL || '';
    const check = (provider, setter) =>
      fetch(`${backendUrl}/api/auth/${provider}/status`)
        .then(r => r.json())
        .then(d => { if (alive) setter(Boolean(d?.configured)); })
        .catch(() => { if (alive) setter(false); });
    check('google', setGoogleSsoReady);
    check('microsoft', setMicrosoftSsoReady);
    return () => { alive = false; };
  }, []);

  const anySso = googleSsoReady || microsoftSsoReady;

  // "Më mbaj të kyçur" i kyçur si parazgjedhje — pa të, sesioni fshihet kur
  // mbyllet browseri (ose kur rihapet paneli i preview-t nga IDE), gjë që
  // detyronte login në çdo rihapje. Përdoruesi mund ta çkyçë kur të dojë.
  const { register, handleSubmit, setValue: setLoginValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { Remember: true },
  });
  const {
    register: rt, handleSubmit: hts, reset: rr, watch: wt,
    setValue: sv, formState: { errors: te },
  } = useForm({ resolver: yupResolver(tenantSchema), defaultValues: { TenantName:'', TenantEmail:'', TenantSlug:'', TenantPassword:'', TenantPhone:'', TenantCity:'', TenantAddress:'', TenantManager:'' } });

  const tenantName = wt('TenantName');
  const tenantSlug = wt('TenantSlug');

  const checkSlug = useCallback(
    (slug) => checkSlugAvailability(slug, setSlugStatus, setSlugMessage), []
  );

  useEffect(() => {
    if (!tenantName || tenantSlugManual) return;
    let s = tenantName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (s && /^[0-9]/.test(s)) s = 'company-' + s;
    sv('TenantSlug', s);
  }, [tenantName, tenantSlugManual, sv]);

  useEffect(() => {
    if (!tenantSlug || tenantSlug.length < 3) { setSlugStatus(null); setSlugMessage(''); return; }
    const t = setTimeout(() => checkSlug(tenantSlug), 500);
    return () => clearTimeout(t);
  }, [tenantSlug, checkSlug]);

  // Kthimi nga linku i konfirmimit. Serveri e çon njeriun te `#/login?confirm=…`
  // pas klikimit, dhe pa këtë ai do të mbërrinte te një ekran kyçjeje pa asnjë
  // shenjë se konfirmimi i tij bëri gjë — pra do ta klikonte sërish.
  useEffect(() => {
    const q = new URLSearchParams((window.location.hash.split('?')[1]) || window.location.search);
    const state = q.get('confirm');
    if (!state) return;
    const shown = {
      ok:      { message: t('login.confirmOk'),      variant: 'success' },
      invalid: { message: t('login.confirmInvalid'), variant: 'error' },
      missing: { message: t('login.confirmMissing'), variant: 'error' },
      failed:  { message: t('login.confirmFailed'),  variant: 'error' },
      error:   { message: t('login.confirmError'),   variant: 'error' },
    }[state];
    if (shown) setToast({ open: true, ...shown });
    const mail = q.get('email');
    if (state === 'ok' && mail) setLoginValue('Email', mail);
    // Parametri hiqet, që një rifreskim të mos e shfaqë sërish të njëjtin mesazh.
    window.history.replaceState(null, '', window.location.pathname + '#/login');
  }, [t, setLoginValue]);

  // Nga faqja publike: pas hapjes së kompanisë, kyçja vjen me email-in e mbushur.
  useEffect(() => {
    if (initialEmail) setLoginValue('Email', initialEmail);
  }, [initialEmail, setLoginValue]);

  // Guard sinkron kundër double-submit (useRef nuk pret re-render)
  const submittingRef = React.useRef(false);

  // ── Login ──────────────────────────────────────────────────────────────────
  // Kur email-i ka llogari në disa kompani, serveri kërkon zgjedhjen; kredencialet
  // mbahen deri sa të zgjidhet, pa u shkruar askund.
  const [companyPick, setCompanyPick] = useState(null);
  const [pickingSlug, setPickingSlug] = useState(null);

  const onSubmit = async (login, tenant) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setToast({ open: false });
    const backendUrl = process.env.REACT_APP_API_URL || '';
    try {
      const res  = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: login.Email, password: login.Password, ...(tenant ? { tenant } : {}) }),
      });
      const data = await res.json();
      if (data?.success && data?.needsCompany) {
        const companies = data?.data?.companies || [];
        if (companies.length) setCompanyPick({ login, companies });
        else setToast({ open: true, message: t('login.errorWrongCredentials'), variant: 'error' });
      } else if (data?.success && data?.data?.user) {
        setCompanyPick(null);
        const u = data.data.user;
        const session = {
          success: true,
          data: { id: u.id, username: u.username || u.email.split('@')[0], firstname: u.firstname, lastname: u.lastname, email: u.email, roles: u.roles || [u.user_role || 'admin'], worker_id: u.worker_id || null, WorkerId: u.worker_id || null },
          roles: u.roles || [u.user_role || 'admin'],
          token: u.token,
          company: 'hr',
          subscription: null,
          WorkerId: u.worker_id || null,
          worker_id: u.worker_id || null,
        };
        // Desktop (Electron): ruaj sesionin afatgjatë → kyçje vetëm herën e parë.
        persistLogedInUser(JSON.stringify(session), login.Remember);
        window.location = '/';
      } else {
        setCompanyPick(null);
        setToast({ open: true, message: data?.error || t('login.errorWrongCredentials'), variant: 'error' });
      }
    } catch {
      setToast({ open: true, message: t('login.errorServerDown'), variant: 'error' });
    } finally {
      setLoading(false);
      setPickingSlug(null);
      submittingRef.current = false;
    }
  };

  const pickCompany = (slug) => {
    if (!companyPick) return;
    setPickingSlug(slug);
    onSubmit(companyPick.login, slug);
  };

  // ── Krijo kompani ──────────────────────────────────────────────────────────
  // Serveri e hap kompaninë brenda vetë kërkesës (shih `POST /api/tenants/request`),
  // ndaj këtu nuk ka më asgjë për të pritur: email-i mbushet te forma e kyçjes
  // dhe njeriu hyn me fjalëkalimin që sapo zgjodhi.
  const onCreateTenant = async (v) => {
    setTenantLoading(true);
    try {
      const resp = await apiService().post('tenants/request', {
        name:           v.TenantName,
        slug:           v.TenantSlug || '',
        contact_email:  v.TenantEmail,
        admin_password: v.TenantPassword,
        phone:          v.TenantPhone   || undefined,
        city:           v.TenantCity    || undefined,
        address:        v.TenantAddress || undefined,
        contact_person: v.TenantManager || undefined,
      });
      if (resp?.success) {
        // 202 = pranuar, e paverifikuar. Kompania krijohet me klikimin e linkut,
        // ndaj mesazhi nuk guxon të thotë «u hap».
        const pending = resp?.data?.requiresConfirmation;
        setToast({
          open: true,
          message: t(pending ? 'login.companyVerifySent' : 'login.companyCreatedSuccess'),
          variant: 'success',
        });
        setLoginValue('Email', resp?.data?.email || v.TenantEmail);
        rr(); setSlugStatus(null); setSlugMessage(''); setTenantSlugManual(false); setTenantModal(false);
      } else {
        setToast({ open: true, message: resp?.error || t('login.companyCreateFailed'), variant: 'error' });
      }
    } catch (err) {
      setToast({ open: true, message: err?.message || 'Gabim.', variant: 'error' });
    } finally {
      setTenantLoading(false);
    }
  };

  // Kick off a provider's hosted sign-in. Desktop opens the system browser
  // (returns via deep-link); web navigates in place.
  const startSso = (provider) => {
    const api = process.env.REACT_APP_API_URL || '';
    if (window.electronAPI?.openExternal) {
      window.electronAPI.openExternal(`${api}/api/auth/${provider}/start?surface=desktop`);
    } else {
      window.location.href = `${api}/api/auth/${provider}/start?surface=web`;
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  RENDER — Splash-gradient centered card (Nexuma Design System · brand moment)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: { xs: 2.5, sm: 3 },
      boxSizing: 'border-box',
      fontFamily: 'var(--nx-font-body, "Inter", system-ui, sans-serif)',
      background: 'linear-gradient(135deg, #2D3D52 0%, #3D8FA3 100%)',
      paddingTop: { xs: 'max(20px, env(safe-area-inset-top))', md: 3 },
      paddingBottom: { xs: 'max(20px, env(safe-area-inset-bottom))', md: 3 },
    }}>
      <Toast {...toast} />

      <Box sx={{ width: 400, maxWidth: '100%' }}>

        {/* ── Back to landing (web only) ── */}
        {onBack && !isElectron() && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              onClick={onBack}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.7,
                px: 1.6, py: 0.7, borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.22)',
                bgcolor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.9)', cursor: 'pointer',
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.4)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 15 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1, color: 'inherit' }}>
                {t('login.backHome')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Brand lockup ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 2.75 }}>
          <Box sx={{
            width: 58, height: 58, borderRadius: '14px', bgcolor: '#2D3D52',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 20px 48px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.06)',
          }}>
            <NexumiLogo height={34} variant="mono" showWordmark={false} />
          </Box>
          <Typography sx={{ fontFamily: 'var(--nx-font-display, "Funnel Display", "Inter", sans-serif)', fontSize: '1.5rem', fontWeight: 800, letterSpacing: '0.6px', color: '#fff', lineHeight: 1.1 }}>
            Nexuma
          </Typography>
          <Typography sx={{ fontFamily: 'var(--nx-font-display, "Funnel Display", "Inter", sans-serif)', fontSize: '0.625rem', fontWeight: 500, letterSpacing: '1.4px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.78)' }}>
            Burime Njerëzore
          </Typography>
        </Box>

        {/* ── Auth card ── */}
        <Box sx={{
          bgcolor: '#fff',
          borderRadius: '14px',
          p: { xs: 2.5, sm: 3 },
          boxShadow: '0 24px 48px rgba(17,24,39,0.28), 0 8px 16px rgba(17,24,39,0.12)',
        }}>
          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.015em', color: 'text.primary', mb: 0.25 }}>
            {t('login.welcome')}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 2.25 }}>
            {t('login.welcomeSubtitle')}
          </Typography>

          <CompanyPickerDialog
            open={Boolean(companyPick)}
            companies={companyPick?.companies || []}
            busySlug={pickingSlug}
            onPick={pickCompany}
            onClose={() => setCompanyPick(null)}
          />
          <Box component="form" onSubmit={handleSubmit((v) => onSubmit(v))} noValidate>
            <Stack spacing={2}>
              {/* Email / worker ID */}
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.7 }}>
                  {t('login.emailOrIdLabel')}
                </Typography>
                <TextField
                  placeholder="emri@kompania.com  ose  EMP-00001"
                  fullWidth size="small" autoComplete="username" autoFocus
                  {...register('Email')}
                  error={!!errors.Email} helperText={errors.Email?.message}
                  InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }}
                />
              </Box>

              {/* Password */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.7 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                    {t('login.passwordLabel')}
                  </Typography>
                  <Typography onClick={() => setForgotModal(true)} sx={{ fontSize: '0.75rem', color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.dark', textDecoration: 'underline' } }}>
                    {t('login.forgotPassword')}
                  </Typography>
                </Box>
                <TextField
                  placeholder="••••••••••"
                  type={showPass ? 'text' : 'password'}
                  fullWidth size="small" autoComplete="current-password"
                  {...register('Password')}
                  error={!!errors.Password} helperText={errors.Password?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPass(p => !p)} edge="end" tabIndex={-1}>
                          {showPass ? <VisibilityOffIcon sx={{ fontSize: 17 }} /> : <VisibilityIcon sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Remember */}
              <FormControlLabel
                control={<Checkbox size="small" sx={{ p: 0.5 }} {...register('Remember')} />}
                label={<Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', ml: 0.5 }}>{t('login.rememberMe')}</Typography>}
                sx={{ m: 0, mt: -0.75 }}
              />

              {/* Submit */}
              <Button
                type="submit" fullWidth size="large" variant="contained" disabled={loading}
                sx={{ minHeight: { xs: 48, md: 44 } }}
              >
                {loading
                  ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={17} sx={{ color: 'rgba(255,255,255,0.8)' }} /><span>{t('login.submitting')}</span></Box>
                  : t('login.submitBtn')}
              </Button>
            </Stack>
          </Box>

          {/* Alternate sign-in / registration */}
          {(anySso || !isElectron()) && (
            <Divider sx={{ my: 2.25 }}>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {t('login.or')}
              </Typography>
            </Divider>
          )}

          <Stack spacing={1.25}>
            {googleSsoReady && (
              <Button
                fullWidth variant="outlined" onClick={() => startSso('google')}
                startIcon={<GoogleIcon sx={{ fontSize: 18, color: '#4285F4' }} />}
                sx={{ minHeight: { xs: 46, md: 42 } }}
              >
                {t('login.signInWithGoogle')}
              </Button>
            )}

            {microsoftSsoReady && (
              <Button
                fullWidth variant="outlined" onClick={() => startSso('microsoft')}
                startIcon={<MicrosoftMark />}
                sx={{ minHeight: { xs: 46, md: 42 } }}
              >
                {t('login.signInWithMicrosoft', 'Hyr me Microsoft')}
              </Button>
            )}

            {!isElectron() && (
              <Button
                fullWidth variant="outlined" color="primary"
                onClick={() => setTenantModal(true)}
                startIcon={<DomainAddIcon sx={{ fontSize: 17 }} />}
                sx={{ minHeight: { xs: 46, md: 42 } }}
              >
                {t('login.createCompany')}
              </Button>
            )}
          </Stack>
        </Box>

        {/* ── Footer ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <LockOutlinedIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>{t('login.aesEncryption')}</Typography>
          </Box>
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.35)' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>GDPR</Typography>
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.35)' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem', fontWeight: 600 }}>www.nexuma.app</Typography>
        </Box>
        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', mt: 1.25, fontSize: '0.7rem' }}>
          © {new Date().getFullYear()} Nexuma · All rights reserved
        </Typography>
      </Box>

      {/* ══ PWA INSTALL BANNER ══ */}
      {isInstallable && !isInStandaloneMode && pwaVisible && (
        <Slide direction="up" in mountOnEnter unmountOnExit>
          <Box sx={{
            position: 'fixed',
            bottom: { xs: 'max(16px, env(safe-area-inset-bottom))', md: 24 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            width: { xs: 'calc(100% - 32px)', sm: 420 },
            maxWidth: 480,
            bgcolor: 'grey.900',
            borderRadius: '14px',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
          }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NexumiLogo height={22} variant="mono" showWordmark={false} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{t('login.installTitle')}</Typography>
              <Typography sx={{ color: 'grey.400', fontSize: '0.73rem', mt: 0.3 }}>
                {needsManual && isIOS ? t('login.installIosHint') : t('login.installHint')}
              </Typography>
            </Box>
            {!needsManual && (
              <Button
                size="small" variant="contained"
                startIcon={<GetAppIcon sx={{ fontSize: 15 }} />}
                onClick={async () => { await install(); setPwaVisible(false); }}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {t('login.installBtn')}
              </Button>
            )}
            {needsManual && isIOS && (
              <IosShareIcon sx={{ color: 'primary.light', fontSize: 22, flexShrink: 0 }} />
            )}
            <IconButton size="small" onClick={() => setPwaVisible(false)} sx={{ color: 'grey.500', flexShrink: 0, p: 0.5, '&:hover': { color: 'grey.300' } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Slide>
      )}

      {/* ── Dialog: Krijo Kompani ── */}
      <Dialog open={tenantModal} onClose={() => setTenantModal(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          {t('login.registerCompanyTitle')}
          <Box component="span" sx={{
            fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.02em',
            color: '#1D5462', bgcolor: '#E4F0F3', border: '1px solid #C6E0E6',
            borderRadius: 999, px: 1.2, py: 0.35, whiteSpace: 'nowrap',
          }}>{t('login.trialBadgeFull')}</Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('login.verifyNote')}
          </Typography>
          {/* Slug hidden – auto-generated */}
          <input type="hidden" {...rt('TenantSlug')} />

          {/* Pesë fusha, njësoj si te faqja publike. */}
          <Stack spacing={2}>
            <TextField label={`${t('login.companyName')} *`} fullWidth size="small" {...rt('TenantName')} error={!!te.TenantName} helperText={te.TenantName?.message} />
            <TextField label={`${t('login.contactPerson')} *`} fullWidth size="small" placeholder="p.sh. Fisnik Sejdiu" {...rt('TenantManager')} error={!!te.TenantManager} helperText={te.TenantManager?.message} />
            <TextField label={`${t('login.contactEmail')} *`} type="email" fullWidth size="small" {...rt('TenantEmail')} error={!!te.TenantEmail} helperText={te.TenantEmail?.message} />
            <TextField label={`${t('login.phone')} *`} fullWidth size="small" {...rt('TenantPhone')} error={!!te.TenantPhone} helperText={te.TenantPhone?.message} />
            <TextField label={`${t('login.adminPassword')} *`} type="password" fullWidth size="small" {...rt('TenantPassword')} error={!!te.TenantPassword} helperText={te.TenantPassword?.message} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantModal(false)} variant="outlined">{t('common.cancel')}</Button>
          <Button variant="contained" disabled={tenantLoading} onClick={hts(onCreateTenant)}>
            {tenantLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('login.submitRequest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Forgot Password ── */}
      <ForgotPassword open={forgotModal} onClose={() => setForgotModal(false)} />
    </Box>
  );
}
